import { useCallback, useEffect, useState } from "react";

/* ==========================================================
   CONFIG  (apni baaki pages jaisa API base yahan set karo)
========================================================== */
const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const RESORT_API = `${API_BASE}/api/resorts`;
const UPLOAD_API = `${API_BASE}/api/upload`;
const UPLOAD_FIELD = "file"; // Upload.routes: .single("file")  (ek request = ek file)

const PROPERTY_TYPES = [
  ["resort", "Resort"],
  ["luxury-resort", "Luxury Resort"],
  ["beach-resort", "Beach Resort"],
  ["hill-resort", "Hill Resort"],
  ["eco-resort", "Eco Resort"],
  ["farm-resort", "Farm Resort"],
  ["spa-resort", "Spa Resort"],
  ["other", "Other"],
];

const AMENITY_OPTIONS = [
  "Free Wi-Fi", "Parking", "Air Conditioning", "Swimming Pool", "Restaurant", "Bar",
  "Spa & Wellness", "Gym", "Room Service", "Kids Play Area", "Bonfire", "Adventure Activities",
  "Conference Hall", "Banquet Hall", "Airport Transfer", "Power Backup", "Laundry", "Pet Friendly",
];


const EMPTY_FORM = {
  name: "",
  propertyType: "resort",
  starRating: 3,
  shortDescription: "",
  description: "",
  contact: { phone: "", alternatePhone: "", email: "" },
  address: {
    addressLine: "", landmark: "", city: "", state: "",
    country: "India", pincode: "", latitude: "", longitude: "",
  },
  checkInTime: "14:00",
  checkOutTime: "11:00",
  isActive: true,
  amenities: [],
  images: [],
  coverImage: { url: "", publicId: "" },
  policies: {
    cancellationPolicy: "", childPolicy: "", petPolicy: "",
    smokingPolicy: "", idProofRequired: true,
  },
};

/* ==========================================================
   HELPERS
========================================================== */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vendorToken") || ""}` });

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}), ...options.headers },
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (res.status === 401 && data.code === "TOKEN_EXPIRED") {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
  }
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

// Upload response alag-alag shape me aa sakta hai, sab handle kiya hai
function extractUploaded(data) {
  const raw = data.file || data.files || data.images || data.urls || data.data || data.result || data.url || data.secure_url || data;
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((f) =>
      typeof f === "string"
        ? { url: f, publicId: "" }
        : { url: f?.url || f?.secure_url || f?.fileUrl || f?.path || "", publicId: f?.publicId || f?.public_id || "" }
    )
    .filter((f) => f.url);
}

const merge = (base, incoming) => {
  const out = { ...base };
  Object.keys(base).forEach((k) => {
    const b = base[k];
    const v = incoming?.[k];
    if (v === undefined || v === null) return;
    out[k] = b && typeof b === "object" && !Array.isArray(b) ? merge(b, v) : v;
  });
  return out;
};

const toNum = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

function validate(f) {
  if (!f.name.trim()) return "Resort name is required.";
  if (f.contact.phone && !/^[0-9+\-\s]{8,15}$/.test(f.contact.phone)) return "Enter a valid phone number.";
  if (f.contact.email && !/^\S+@\S+\.\S+$/.test(f.contact.email)) return "Enter a valid email address.";
  if (f.address.pincode && !/^[0-9]{4,10}$/.test(f.address.pincode)) return "Enter a valid pincode.";
  const lat = toNum(f.address.latitude);
  const lng = toNum(f.address.longitude);
  if (lat !== null && (lat < -90 || lat > 90)) return "Latitude must be between -90 and 90.";
  if (lng !== null && (lng < -180 || lng > 180)) return "Longitude must be between -180 and 180.";
  return "";
}

/* ==========================================================
   SMALL UI PIECES
========================================================== */
function Input({ label, value, onChange, type = "text", placeholder, required, ...rest }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{required && " *"}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
    </div>
  );
}

function Area({ label, value, onChange, rows = 4, placeholder, maxLength }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{maxLength ? ` (${value.length}/${maxLength})` : ""}</label>
      <textarea rows={rows} value={value} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Notice({ kind, children }) {
  if (!children) return null;
  const bad = kind === "error";
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
        background: bad ? "#fef2f2" : "#f0fdf4", color: bad ? "#991b1b" : "#166534",
        border: `1px solid ${bad ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      {children}
    </div>
  );
}

/* ==========================================================
   MAIN COMPONENT
========================================================== */
export default function AddResort() {
  const [view, setView] = useState("list"); // list | form
  const [resorts, setResorts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [customAmenity, setCustomAmenity] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const setTop = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const setNested = (group, key, val) => setForm((p) => ({ ...p, [group]: { ...p[group], [key]: val } }));

  /* ---------- LIST ---------- */
  const loadResorts = useCallback(async () => {
    setLoadingList(true);
    setError("");
    try {
      const data = await api(`${RESORT_API}/my-resorts`);
      const list = data.resorts || data.data || [];
      setResorts(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadResorts(); }, [loadResorts]);

  /* ---------- OPEN FORM ---------- */
  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setSuccess("");
    setView("form");
  };

  const startEdit = async (id) => {
    setError("");
    setSuccess("");
    try {
      const data = await api(`${RESORT_API}/${id}`);
      const r = data.resort || data.data || data;
      setForm(merge(EMPTY_FORM, r));
      setEditingId(id);
      setView("form");
    } catch (e) {
      setError(e.message);
    }
  };

  /* ---------- AMENITIES ---------- */
  const toggleAmenity = (name) =>
    setTop("amenities", form.amenities.includes(name) ? form.amenities.filter((a) => a !== name) : [...form.amenities, name]);

  const addCustomAmenity = () => {
    const v = customAmenity.trim();
    if (!v || form.amenities.some((a) => a.toLowerCase() === v.toLowerCase())) return setCustomAmenity("");
    setTop("amenities", [...form.amenities, v]);
    setCustomAmenity("");
  };

  /* ---------- IMAGES ---------- */
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (form.images.length + files.length > 30) return setError("You can add up to 30 images.");
    if (files.some((f) => !f.type.startsWith("image/"))) return setError("Only image files are allowed here.");
    if (files.some((f) => f.size > 10 * 1024 * 1024)) return setError("Each image must be under 10 MB.");

    setUploading(true);
    setError("");

    // Backend ek request me sirf ek file leta hai, isliye ek-ek karke upload
    const uploaded = [];
    let failed = 0;
    let lastError = "";

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append(UPLOAD_FIELD, file);
        const data = await api(UPLOAD_API, { method: "POST", body: fd });
        const items = extractUploaded(data);
        if (!items.length) throw new Error("Upload worked but no image URL came back from the server.");
        uploaded.push(items[0]);
      } catch (err) {
        failed += 1;
        lastError = err.message;
      }
    }

    if (uploaded.length) {
      setForm((p) => ({
        ...p,
        images: [...p.images, ...uploaded],
        coverImage: p.coverImage.url ? p.coverImage : uploaded[0],
      }));
    }
    if (failed) setError(`${failed} of ${files.length} photos failed to upload: ${lastError}`);
    setUploading(false);
  };

  const removeImage = (i) =>
    setForm((p) => {
      const removed = p.images[i];
      const images = p.images.filter((_, x) => x !== i);
      const coverImage = p.coverImage.url === removed.url ? images[0] || { url: "", publicId: "" } : p.coverImage;
      return { ...p, images, coverImage };
    });

  /* ---------- SAVE (no admin approval - goes live directly) ---------- */
  const save = async () => {
    const problem = validate(form);
    if (problem) { setError(problem); setSuccess(""); return; }

    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      ...form,
      name: form.name.trim(),
      starRating: Number(form.starRating) || 3,
      address: { ...form.address, latitude: toNum(form.address.latitude), longitude: toNum(form.address.longitude) },
    };

    try {
      if (editingId) {
        await api(`${RESORT_API}/${editingId}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await api(RESORT_API, { method: "POST", body: JSON.stringify(payload) });
      }
      await loadResorts();
      setView("list");
      setSuccess(editingId ? "Resort updated." : "Resort added and live.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api(`${RESORT_API}/${r._id}`, { method: "DELETE" });
      setSuccess("Resort deleted.");
      loadResorts();
    } catch (e) {
      setError(e.message);
    }
  };

  /* ==========================================================
     LIST VIEW
  ========================================================== */
  if (view === "list") {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div>
            <h2>My Resorts</h2>
            <p>Add your resorts and keep their details up to date. Changes go live immediately.</p>
          </div>
          <button className="bmt-primary-btn" onClick={startNew}>+ Add Resort</button>
        </div>

        <Notice kind="error">{error}</Notice>
        <Notice kind="success">{success}</Notice>

        <section className="bmt-form-card">
          {loadingList ? (
            <p>Loading your resorts...</p>
          ) : resorts.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No resorts yet</h3>
              <p>Add your first resort with photos, amenities and policies to start receiving bookings.</p>
              <button className="bmt-primary-btn" onClick={startNew}>Add your first resort</button>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {resorts.map((r) => (
                <article
                  key={r._id}
                  style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: 14, border: "1px solid #e5e7eb", borderRadius: 10 }}
                >
                  <div style={{ width: 120, height: 80, borderRadius: 8, overflow: "hidden", background: "#f3f4f6", flexShrink: 0 }}>
                    {(r.coverImage?.url || r.images?.[0]?.url) && (
                      <img src={r.coverImage?.url || r.images[0].url} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <strong style={{ fontSize: 16 }}>{r.name}</strong>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                      {[r.address?.city, r.address?.state].filter(Boolean).join(", ") || "Location not added"} • {r.starRating}★
                    </div>
                    <span style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, color: r.isActive ? "#15803d" : "#6b7280" }}>
                      {r.isActive ? "Live" : "Hidden from guests"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="bmt-secondary-btn" onClick={() => startEdit(r._id)}>Edit</button>
                    <button className="bmt-secondary-btn" onClick={() => remove(r)}>Delete</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  /* ==========================================================
     FORM VIEW
  ========================================================== */
  return (
    <div className="bmt-premium-page">
      <div className="bmt-section-intro">
        <div>
          <h2>{editingId ? "Edit Resort" : "Add Resort"}</h2>
          <p>Fill in the details guests will see. Your resort goes live as soon as you save.</p>
        </div>
        <button className="bmt-secondary-btn" onClick={() => { setView("list"); setError(""); }}>Back to list</button>
      </div>

      <Notice kind="error">{error}</Notice>

      {/* BASIC */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Basic information</h3></div>
        <div className="bmt-form-grid">
          <Input label="Resort name" required value={form.name} onChange={(v) => setTop("name", v)} placeholder="e.g. Blue Lagoon Resort" />
          <div className="bmt-field-group">
            <label>Property type</label>
            <select value={form.propertyType} onChange={(e) => setTop("propertyType", e.target.value)}>
              {PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="bmt-field-group">
            <label>Star rating</label>
            <select value={form.starRating} onChange={(e) => setTop("starRating", Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} star</option>)}
            </select>
          </div>
        </div>
        <Area label="Short description" rows={3} maxLength={500} value={form.shortDescription} onChange={(v) => setTop("shortDescription", v)} placeholder="One or two lines that appear in search results" />
        <Area label="Full description" rows={6} value={form.description} onChange={(v) => setTop("description", v)} placeholder="Describe the property, surroundings, food and experiences" />
      </section>

      {/* CONTACT */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Contact details</h3></div>
        <div className="bmt-form-grid">
          <Input label="Phone" value={form.contact.phone} onChange={(v) => setNested("contact", "phone", v)} placeholder="+91..." />
          <Input label="Alternate phone" value={form.contact.alternatePhone} onChange={(v) => setNested("contact", "alternatePhone", v)} placeholder="+91..." />
          <Input label="Email" type="email" value={form.contact.email} onChange={(v) => setNested("contact", "email", v)} placeholder="resort@example.com" />
        </div>
      </section>

      {/* LOCATION */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Location</h3></div>
        <div className="bmt-form-grid">
          <Input label="Address" value={form.address.addressLine} onChange={(v) => setNested("address", "addressLine", v)} placeholder="Street / village / area" />
          <Input label="Landmark" value={form.address.landmark} onChange={(v) => setNested("address", "landmark", v)} placeholder="Near..." />
          <Input label="City" value={form.address.city} onChange={(v) => setNested("address", "city", v)} />
          <Input label="State" value={form.address.state} onChange={(v) => setNested("address", "state", v)} />
          <Input label="Country" value={form.address.country} onChange={(v) => setNested("address", "country", v)} />
          <Input label="Pincode" value={form.address.pincode} onChange={(v) => setNested("address", "pincode", v)} />
          <Input label="Latitude" type="number" step="any" value={form.address.latitude ?? ""} onChange={(v) => setNested("address", "latitude", v)} placeholder="e.g. 30.7046" />
          <Input label="Longitude" type="number" step="any" value={form.address.longitude ?? ""} onChange={(v) => setNested("address", "longitude", v)} placeholder="e.g. 76.7179" />
        </div>
        <div className="bmt-form-grid">
          <Input label="Check-in from" type="time" value={form.checkInTime} onChange={(v) => setTop("checkInTime", v)} />
          <Input label="Check-out until" type="time" value={form.checkOutTime} onChange={(v) => setTop("checkOutTime", v)} />
        </div>
      </section>

      {/* AMENITIES */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Amenities</h3><p>{form.amenities.length} selected</p></div>
        <div className="bmt-check-grid">
          {AMENITY_OPTIONS.map((item) => (
            <label className={`bmt-check-card ${form.amenities.includes(item) ? "selected" : ""}`} key={item}>
              <input type="checkbox" checked={form.amenities.includes(item)} onChange={() => toggleAmenity(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <div className="bmt-field-group">
          <label>Add another amenity</label>
          <div className="bmt-inline-input">
            <input
              value={customAmenity}
              placeholder="e.g. Private beach access"
              onChange={(e) => setCustomAmenity(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAmenity(); } }}
            />
            <button type="button" onClick={addCustomAmenity}>Add</button>
          </div>
        </div>
        <div className="bmt-chip-wrap">
          {form.amenities.filter((a) => !AMENITY_OPTIONS.includes(a)).map((a) => (
            <span className="bmt-chip" key={a}>
              {a}
              <button type="button" aria-label={`Remove ${a}`} onClick={() => toggleAmenity(a)}>×</button>
            </span>
          ))}
        </div>
      </section>

      {/* IMAGES */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head">
          <h3>Photos</h3>
          <p>Add at least 8 clear landscape photos. Click a photo to make it the cover image.</p>
        </div>
        <label className="bmt-upload-zone">
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload} disabled={uploading} />
          <span className="bmt-upload-icon">＋</span>
          <strong>{uploading ? "Uploading..." : "Upload resort photos"}</strong>
          <small>JPG, PNG or WEBP, up to 20 MB each</small>
        </label>
        {form.images.length > 0 && (
          <div className="bmt-media-grid">
            {form.images.map((img, i) => {
              const isCover = form.coverImage.url === img.url;
              return (
                <div className="bmt-media-item" key={`${img.url}-${i}`} style={isCover ? { outline: "3px solid #15803d", borderRadius: 8 } : undefined}>
                  <img
                    src={img.url}
                    alt={`Resort photo ${i + 1}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setTop("coverImage", { url: img.url, publicId: img.publicId || "" })}
                  />
                  {isCover && <small style={{ display: "block", textAlign: "center", color: "#15803d", fontWeight: 600 }}>Cover photo</small>}
                  <button type="button" onClick={() => removeImage(i)}>Remove</button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* POLICIES */}
      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Policies</h3></div>
        <Area label="Cancellation policy" value={form.policies.cancellationPolicy} onChange={(v) => setNested("policies", "cancellationPolicy", v)} placeholder="Free cancellation up to 48 hours before check-in..." />
        <Area label="Child policy" rows={3} value={form.policies.childPolicy} onChange={(v) => setNested("policies", "childPolicy", v)} placeholder="Children below 5 stay free..." />
        <Area label="Pet policy" rows={3} value={form.policies.petPolicy} onChange={(v) => setNested("policies", "petPolicy", v)} />
        <Area label="Smoking policy" rows={3} value={form.policies.smokingPolicy} onChange={(v) => setNested("policies", "smokingPolicy", v)} />
        <label className={`bmt-check-card ${form.policies.idProofRequired ? "selected" : ""}`} style={{ maxWidth: 320 }}>
          <input type="checkbox" checked={form.policies.idProofRequired} onChange={(e) => setNested("policies", "idProofRequired", e.target.checked)} />
          <span>Guests must show ID proof at check-in</span>
        </label>
      </section>

      <section className="bmt-form-card">
        <div className="bmt-form-card-head"><h3>Visibility</h3></div>
        <label className={`bmt-check-card ${form.isActive ? "selected" : ""}`} style={{ maxWidth: 360 }}>
          <input type="checkbox" checked={form.isActive} onChange={(e) => setTop("isActive", e.target.checked)} />
          <span>Show this resort to guests</span>
        </label>
      </section>

      <div className="bmt-form-actions">
        <button className="bmt-secondary-btn" disabled={saving} onClick={() => { setView("list"); setError(""); }}>
          Cancel
        </button>
        <button className="bmt-primary-btn" disabled={saving || uploading} onClick={save}>
          {saving ? "Saving..." : editingId ? "Save changes" : "Add resort"}
        </button>
      </div>
    </div>
  );
}